import { useEffect, useRef } from "react";

const steps = [
  {
    number: "01",
    title: "System Audit & Opportunity Mapping",
    desc: "We analyze your current workflows, identify revenue leakage points, and map every automation opportunity across your operation.",
    duration: "2–3 days",
  },
  {
    number: "02",
    title: "Architecture & Build",
    desc: "Custom system design and build based on your operational needs — no off-the-shelf solutions, no unnecessary complexity.",
    duration: "2–4 weeks",
  },
  {
    number: "03",
    title: "Integration & Deployment",
    desc: "Seamless integration with your existing CRM, tools, and data sources. Full deployment with zero disruption to active operations.",
    duration: "1–2 weeks",
  },
  {
    number: "04",
    title: "Optimization & Scale",
    desc: "Ongoing performance monitoring, system refinement, and scale support as your operational needs grow and evolve.",
    duration: "Continuous",
  },
];

export default function ProcessSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll<HTMLElement>("[data-reveal]");
            els.forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = "1";
                el.style.transform = "translateY(0)";
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" className="bg-white" ref={sectionRef}>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-32">
        {/* Centered header */}
        <div className="text-center mb-20">
          <p
            data-reveal
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bfx-accent mb-4"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Process
          </p>
          <h2
            data-reveal
            className="text-4xl md:text-5xl lg:text-[58px] font-semibold text-bfx-primary leading-[1.05] tracking-tight mb-5"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Structured Deployment.
          </h2>
          <p
            data-reveal
            className="text-base text-bfx-muted max-w-md mx-auto leading-relaxed"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            A repeatable, precision-engineered system for deploying financial intelligence infrastructure — on time, on scope.
          </p>
        </div>

        {/* Horizontal steps — Apple-like timeline on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-bfx-border z-0" />

          {steps.map((step, i) => (
            <div
              key={step.number}
              data-reveal
              className="relative flex flex-col items-center md:items-start px-6 pb-10 md:pb-0 text-center md:text-left"
              style={{ opacity: 0, transform: "translateY(20px)" }}
            >
              {/* Step circle */}
              <div className="relative z-10 w-16 h-16 rounded-full bg-bfx-light border border-bfx-border flex flex-col items-center justify-center mb-6 shrink-0">
                <span className="text-[10px] font-semibold text-bfx-accent tracking-widest uppercase">{step.number}</span>
              </div>

              {/* Duration badge */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-bfx-accent/10 mb-4">
                <i className="ri-time-line text-[10px] text-bfx-accent" />
                <span className="text-[10px] font-semibold text-bfx-accent">{step.duration}</span>
              </div>

              {/* Title */}
              <h3 className="text-base font-semibold text-bfx-primary leading-snug mb-3">
                {step.title}
              </h3>

              {/* Desc */}
              <p className="text-sm text-bfx-muted leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* Results CTA band */}
        <div
          data-reveal
          id="results"
          className="mt-24 rounded-2xl bg-bfx-light border border-bfx-border p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8"
          style={{ opacity: 0, transform: "translateY(20px)" }}
        >
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bfx-accent mb-2">Proven Results</p>
            <h3 className="text-2xl md:text-3xl font-semibold text-bfx-primary leading-tight">
              From missed opportunities<br className="hidden md:block" /> to controlled growth.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-6 md:gap-12 shrink-0">
            {[
              { val: "+38%", label: "Lead capture" },
              { val: "2.5×", label: "Response time" },
              { val: "+22%", label: "Conversion rate" },
            ].map((m) => (
              <div key={m.label} className="text-center">
                <p className="text-3xl md:text-4xl font-semibold text-bfx-primary">{m.val}</p>
                <p className="text-xs text-bfx-muted mt-1">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}